import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { SectionTag } from '../ui/SectionTag';
const testimonialImages = ['https://cdn.magicpatterns.com/uploads/vH5gbivaZ5Pn3LWifW3jTd/Imagen_de_ChatGPT_27_sept_2026,_12_41_33.webp', 'https://cdn.magicpatterns.com/uploads/tR6Akcsd9e1AyAFiGoxMxg/Imagen_de_ChatGPT_27_sept_2026,_12_45_06.webp', 'https://cdn.magicpatterns.com/uploads/vEa9dTttvs8sa7Xb2zy1xL/Imagen_de_ChatGPT_27_sept_2026,_12_45_11.webp', 'https://cdn.magicpatterns.com/uploads/7nFPTxzAPL9jE8pZkEmcK7/Imagen_de_ChatGPT_27_sept_2026,_12_45_15.webp', 'https://cdn.magicpatterns.com/uploads/36Aj8xpYhj1f6M6Y3TkxZn/Imagen_de_ChatGPT_27_sept_2026,_12_45_18.webp', 'https://cdn.magicpatterns.com/uploads/i6LbXHuQGTk9apfF7n6Mu2/Imagen_de_ChatGPT_27_sept_2026,_12_45_32.webp'];
export function Testimonials() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const lastIndex = testimonialImages.length - 1;
  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    const slide = track?.children[index] as HTMLElement | undefined;
    if (!track || !slide) return;
    const trackRect = track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    track.scrollTo({
      left: track.scrollLeft + (slideRect.left - trackRect.left),
      behavior: 'smooth'
    });
  }, []);
  const goTo = useCallback((index: number) => scrollToIndex(Math.max(0, Math.min(lastIndex, index))), [scrollToIndex, lastIndex]);

  // Keep the dots/arrows in sync with whatever the user drags or swipes to.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const firstSlide = track.children[0] as HTMLElement | undefined;
        if (!firstSlide) return;
        const slideWidth = firstSlide.getBoundingClientRect().width;
        const index = Math.round(track.scrollLeft / slideWidth);
        setActiveIndex(Math.max(0, Math.min(lastIndex, index)));
      });
    };
    track.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      track.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(frame);
    };
  }, [lastIndex]);
  return <section className="bg-bg-secondary py-10 sm:py-14" data-id="element-216" data-mp="4ttp0371r0fi">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6" data-id="element-217" data-mp="9m4ox61sem82">
        <SectionTag icon="💬" data-id="element-218" data-mp="9no38r3vun77">Comunidad real</SectionTag>
        <h2 className="mt-5 font-heading text-3xl font-bold uppercase leading-tight text-white sm:text-4xl" data-id="element-219" data-mp="3r3ntqhupegio">
          Así están usando el material otros entrenadores
        </h2>
      </div>

      <div className="relative mx-auto mt-8 max-w-4xl px-3 sm:px-6" data-id="element-220" data-mp="3i2zhnsoveq8f">
        <div ref={trackRef} role="region" aria-label="Testimonios de entrenadores, capturas de conversaciones reales" tabIndex={0} onKeyDown={e => {
        if (e.key === 'ArrowRight') goTo(activeIndex + 1);
        if (e.key === 'ArrowLeft') goTo(activeIndex - 1);
      }} className="scrollbar-hide flex snap-x snap-mandatory scroll-smooth overflow-x-auto outline-none" data-id="element-221" data-mp="3n19d84eibsuk">
          {testimonialImages.map((src, i) => <div key={src} className="w-full flex-shrink-0 snap-start px-2 sm:w-1/2" data-id="element-222" data-mp="3mdag9xpug7jx" data-mpi={i}>
              <div className="flex h-[420px] items-center justify-center sm:h-[480px]" data-id="element-223" data-mp="3hf0ev5dj0apv">
                <img src={src} alt={`Testimonio de un entrenador usando el material (captura ${i + 1} de ${testimonialImages.length})`} loading="lazy" decoding="async" className="h-full w-full rounded-xl object-contain" data-id="element-224" data-mp="3w3n5424oymvv" />
              </div>
            </div>)}
        </div>

        <button type="button" onClick={() => goTo(activeIndex - 1)} disabled={activeIndex === 0} aria-label="Testimonio anterior" className="absolute left-0 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-bg-primary/90 text-white transition-colors hover:bg-bg-primary disabled:cursor-not-allowed disabled:opacity-30 sm:-translate-x-3" data-id="element-225" data-mp="3xzsix0m4a4s">
          <ChevronLeftIcon size={20} data-id="element-226" data-mp="1fii5cayd62v8" />
        </button>
        <button type="button" onClick={() => goTo(activeIndex + 1)} disabled={activeIndex === lastIndex} aria-label="Siguiente testimonio" className="absolute right-0 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-bg-primary/90 text-white transition-colors hover:bg-bg-primary disabled:cursor-not-allowed disabled:opacity-30 sm:translate-x-3" data-id="element-227" data-mp="1kg9tquzsv6uo">
          <ChevronRightIcon size={20} data-id="element-228" data-mp="34ua99txhddqk" />
        </button>
      </div>

      <div className="mt-5 flex justify-center gap-2" data-id="element-229" data-mp="3ez0cs48j8pww">
        {testimonialImages.map((_, i) => <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Ir al testimonio ${i + 1}`} aria-current={activeIndex === i} className={`h-2 w-2 rounded-full transition-colors ${activeIndex === i ? 'bg-orange' : 'bg-white/20'}`} data-id="element-230" data-mp="3a209g132i19h" data-mpi={i} />)}
      </div>
    </section>;
}